<template>
  <v-navigation-drawer
    id="core-navigation-drawer"
    v-model="drawer"
    :dark="barColor !== 'rgba(255, 255, 255, 1), rgba(255, 255, 255, 0.7)'"
    :expand-on-hover="expandOnHover"
    :right="$vuetify.rtl"
    :src="barImage"
    mobile-break-point="960"
    app
    width="260"
    v-bind="$attrs"
  >
    <template v-slot:img="props">
      <v-img
        :gradient="`to bottom, ${barColor}`"
        v-bind="props"
      />
    </template>

    <v-list-item two-line>
      <v-list-item-content>
        <v-list-item-title class="text-uppercase font-weight-regular display-2">
          <span class="logo-mini">{{ $t('app-name-short') }}</span>
          <span class="logo-normal">{{ $t('app-name-full') }}</span>
        </v-list-item-title>
      </v-list-item-content>
    </v-list-item>

    <v-divider class="mb-1" />

    <v-list
      dense
      nav
    >
      <base-item-group :item="profile" />
    </v-list>

    <v-divider class="mb-2" />

    <v-list
      expand
      nav
    >
      <!-- Style cascading bug  -->
      <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
      <div />

      <template v-for="(item, i) in computedMenuItems">
        <base-item-group
          v-if="item.children"
          :key="`group-${i}`"
          :item="item"
        >
          <!--  -->
        </base-item-group>

        <base-item
          v-else
          :key="`item-${i}`"
          :item="item"
        />
      </template>

      <!-- Style cascading bug  -->
      <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
      <div />
    </v-list>
  </v-navigation-drawer>
</template>

<script>
  // Utilities
  import {
    mapState,
    mapGetters,
  } from 'vuex'

  import md5 from 'md5'
  import { isAdmin, isDemo } from '@/services/roleValidator'
  import config from '@/config/config'

  export default {
    name: 'DashboardCoreDrawer',

    props: {
      expandOnHover: {
        type: Boolean,
        default: false,
      },
    },

    data: () => ({
      menuItems: [
        {
          icon: 'mdi-view-dashboard',
          title: 'dashboard',
          to: '/',
        },
      ],

      oeItems: [
        {
          icon: 'mdi-clipboard-text-multiple',
          title: 'Open Enrollments',
          to: '/openEnrollments',
        },
      ],

      dataManagementItems: [
        {
          group: '/datamanagement',
          icon: 'mdi-database',
          title: 'Data Management',
          children: [
            {
              title: 'Benefit Admin Sites',
              to: 'benefit-admin-sites',
            },
            {
              title: 'Brokers',
              to: 'brokers',
            },
            {
              title: 'Carriers',
              to: 'carriers',
            },
            {
              title: 'Dropdowns',
              to: 'dropdowns',
            },
            {
              title: 'Enrollers',
              to: 'enrollers',
            },
            {
              title: 'Groups',
              to: 'groups',
            },
            {
              title: 'Platforms',
              to: 'platforms',
            },
            {
              title: 'Product Types',
              to: 'product-types',
            },
            {
              title: 'Products',
              to: 'products',
            },
            {
              title: 'Templates',
              to: 'templates',
            },
          ],
        },
      ],

      demoItems: [
        {
          group: '/pages',
          icon: 'mdi-image',
          title: 'pages',
          children: [
            {
              title: 'pricing',
              to: 'pricing',
            },
            {
              title: 'rtl',
              to: 'rtl',
            },
            {
              title: 'timeline',
              to: 'timeline',
            },
            {
              title: 'login',
              to: 'login',
            },
            {
              title: 'register',
              to: 'pricing',
            },
            {
              title: 'lock',
              to: 'lock',
            },
            {
              title: 'user',
              to: 'user',
            },
            {
              title: 'error',
              to: '404',
            },
          ],
        },
        {
          group: '/components',
          icon: 'mdi-view-comfy',
          title: 'components',
          children: [
            {
              title: 'multi',
              group: '',
              children: [
                {
                  title: 'example',
                  href: '#',
                },
              ],
            },
            {
              title: 'buttons',
              to: 'buttons',
            },
            {
              title: 'grid',
              to: 'grid-system',
            },
            {
              title: 'tabs',
              to: 'tabs',
            },
            {
              title: 'notifications',
              to: 'notifications',
            },
            {
              title: 'icons',
              to: 'icons',
            },
            {
              title: 'typography',
              to: 'typography',
            },
          ],
        },
        {
          group: '/forms',
          icon: 'mdi-clipboard-outline',
          title: 'forms',
          children: [
            {
              title: 'rforms',
              to: 'regular',
            },
            {
              title: 'eforms',
              to: 'extended',
            },
            {
              title: 'vforms',
              to: 'validation',
            },
            {
              title: 'wizard',
              to: 'wizard',
            },
          ],
        },
        {
          group: '/tables',
          icon: 'mdi-grid',
          title: 'tables',
          children: [
            {
              title: 'rtables',
              to: 'regular-tables',
            },
            {
              title: 'etables',
              to: 'extended-tables',
            },
            {
              title: 'dtables',
              to: 'data-tables',
            },
          ],
        },
        {
          group: '/maps',
          icon: 'mdi-map-marker',
          title: 'maps',
          children: [
            {
              title: 'google',
              to: 'google-maps',
            },
            {
              title: 'fullscreen',
              to: 'full-screen-map',
            },
          ],
        },
        {
          icon: 'mdi-widgets',
          title: 'widgets',
          to: '/widgets',
        },
        {
          icon: 'mdi-chart-timeline-variant',
          title: 'charts',
          to: '/charts',
        },
        {
          icon: 'mdi-calendar-range',
          title: 'calendar',
          to: '/calendar',
        },
      ],
    }),

    computed: {
      ...mapGetters('oidcStore', ['oidcUser']),

      ...mapState(['barColor', 'barImage']),

      drawer: {
        get () {
          return this.$store.state.drawer
        },
        set (val) {
          this.$store.commit('SET_DRAWER', val)
        },
      },

      computedMenuItems () {
        let menuItems = this.menuItems

        menuItems = menuItems.concat(this.oeItems)

        if (isAdmin(this.oidcUser)) {
          menuItems = menuItems.concat(this.dataManagementItems)
        }

        if (isDemo(this.oidcUser)) {
          menuItems = menuItems.concat(this.demoItems)
        }

        return menuItems.map(this.mapItem)
      },

      profile () {
        let email = ''
        let title = 'unknown'
        let admin = false
        let profileLinks = []

        if (this.oidcUser) {
          email = md5(this.oidcUser.email.trim().toLowerCase())
          title = this.oidcUser.given_name || this.oidcUser.name || this.oidcUser.preferred_username
          admin = isAdmin(this.oidcUser)
        }

        const img = [
          '//www.gravatar.com/avatar/',
          email,
          '?s=80', // 80 pixels
          '&d=mm', // Default (mystery man)
          '&r=pg', // PG Rating
        ]

        if (admin) {
          const adminLinks = [
            {
              href: config.troodyAdminBase,
              title: this.$t('admin'),
            },
          ]

          profileLinks = profileLinks.concat(adminLinks)
        }

        if (isDemo(this.oidcUser)) {
          const demoLinks = [
            {
              to: 'pages/user',
              title: this.$t('settings'),
            },
          ]

          profileLinks = profileLinks.concat(demoLinks)
        }

        const allUserLinks = [
          {
            href: config.troodyAuthBase + 'Manage',
            title: this.$t('my-profile'),
          },
        ]

        return {
          avatar: img.join(''), // 'https://www.gravatar.com/avatar/205e460b479e2e5b48aec07710c08d50.jpg',
          group: '',
          title: title,
          children: profileLinks.concat(allUserLinks),
        }
      },
    },

    watch: {
      '$vuetify.breakpoint.smAndDown' (val) {
        this.$emit('update:expandOnHover', !val)
      },
    },

    methods: {
      mapItem (item) {
        return {
          ...item,
          children: item.children ? item.children.map(this.mapItem) : undefined,
          title: this.$t(item.title),
        }
      },
    },
  }
</script>

<style lang="sass">
  @import '~vuetify/src/styles/tools/_rtl.sass'

  #core-navigation-drawer
    &.v-navigation-drawer--mini-variant
      .v-list-item
        justify-content: flex-start !important

      .v-list-group--sub-group
        display: block !important

    .v-list-group__header.v-list-item--active:before
      opacity: .24

    .v-list-item
      &__icon--text,
      &__icon:first-child
        justify-content: center
        text-align: center
        width: 20px

        +ltr()
          margin-right: 24px
          margin-left: 12px !important

        +rtl()
          margin-left: 24px
          margin-right: 12px !important

    .v-list--dense
      .v-list-item
        &__icon--text,
        &__icon:first-child
          margin-top: 10px

    .v-list-group--sub-group
      .v-list-item
        +ltr()
          padding-left: 8px

        +rtl()
          padding-right: 8px

      .v-list-group__header
        +ltr()
          padding-right: 0

        +rtl()
          padding-right: 0

        .v-list-item__icon--text
          margin-top: 19px
          order: 0

        .v-list-group__header__prepend-icon
          order: 2

          +ltr()
            margin-right: 8px

          +rtl()
            margin-left: 8px
</style>
